import {FC, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
//store
import {getCurrentEventSelector} from '@/store/organization-event';
import {useAppSelector} from '@/store/hooks';
//components
import Loader from '@/shared/components/Loader';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Modal from '@/shared/components/Modal';
import ContactDetails from './ContactDetails';
import ContactEdit from './ContactEdit';
//types
import {IOrganizationContact, IOrganizationContactsListResponse} from '@/utils/types';
//helpers
import {getErrorMessage} from '@/utils/helpers';
import {tableContactsMenuContent} from './helpers';
//icons
import {ReactComponent as ImportIcon} from '@/assets/icons/import-file.svg';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
//services
import {OrganizationContactService} from '@/utils/services';
//constants
import {
	BODY1,
	defaultPage,
	defaultPageSize,
	FIRST_NAME,
	LAST_NAME,
	IMPORT_CONTACT_LIST,
	ID,
	EMAIL,
	PHONE,
	MEAL,
	ERROR,
	CANCEL,
	DELETE,
	EDIT,
	CLOSE,
} from '@/utils/constants';
//enums
import {MENU_OPTION} from '@/utils/enums/tables.enum';
//hooks
import {useGetOrganizationId, useSkipFirstRenderEffect} from '@/utils/hooks';
//styles
import {contactListTableStyle} from './ContactListTable.styles';
import ImportContactList from './ImportContactList';
import {TABLES_ENTITIES} from '@/utils/enums';

interface ContactListTableProps {
	contactsList: IOrganizationContactsListResponse;
}

const ContactListTable: FC<ContactListTableProps> = ({contactsList}) => {
	const [contactsListData, setContactsListData] = useState<IOrganizationContactsListResponse>(contactsList);

	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(true);
	const [page, setPage] = useState<number>(defaultPage);
	const [searchName, setSearchName] = useState<string>('');

	const [deleteContactId, setDeleteContactId] = useState<number | null>(null);
	const [detailsContact, setDetailsContact] = useState<IOrganizationContact | null>(null);

	const [isShowImportContactsModal, setIsShowImportContactsModal] = useState<boolean>(false);
	const [isShowContactDetailsModal, setIsShowContactDetailsModal] = useState<boolean>(false);
	const [isShowContactEditModal, setIsShowContactEditModal] = useState<boolean>(false);
	const [isExitModalOpen, setIsExitModalOpen] = useState(false);

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const organizationId = useGetOrganizationId();

	const {data, pageSize, totalCount} = contactsListData;

	useSkipFirstRenderEffect(() => {
		if (currentEvent) getContactsListData();
	}, [page]);

	const handleImportContactsModalChange = () => {
		setIsShowImportContactsModal(!isShowImportContactsModal);
		if (isShowImportContactsModal) getContactsListData();
	};

	const handleOpenContactDetailsModal = async (contactId?: number) => {
		setIsShowContactDetailsModal(!isShowContactDetailsModal);
		if (!isShowContactDetailsModal && contactId) {
			const contact = await OrganizationContactService.getContact(contactId);
			setDetailsContact(contact.data);
		}
		if (!contactId) getContactsListData();
	};

	const getContactsListData = async (value?: string) => {
		setIsDataLoaded(false);

		try {
			const reqBody = {
				organizationId: organizationId!,
				page,
				searchName: value,
			};

			const contactsListData = await OrganizationContactService.getContactsList(reqBody);
			setContactsListData(contactsListData.data);
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsDataLoaded(true);
		}
	};

	const handleSearchChange = (value: string) => {
		setSearchName(value);
		if (currentEvent) getContactsListData(value);
	};

	const handlePressEnter = () => {
		setPage(defaultPage);
		if (currentEvent) getContactsListData(searchName);
	};

	const handleDeleteContactItem = async () => {
		try {
			await OrganizationContactService.deleteContact(deleteContactId as number);
			await getContactsListData();
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setDeleteContactId(null);
		}
	};

	const handleOpenEditContactModal = () => {
		setIsShowContactDetailsModal(false);
		setIsShowContactEditModal(true);
	};

	const handleCloseContactModals = () => {
		setIsShowContactDetailsModal(false);
		setIsShowContactEditModal(false);
		setDetailsContact(null);

		if (isShowContactEditModal) getContactsListData();
	};

	const handleExitModalChange = () => setIsExitModalOpen(!isExitModalOpen);

	const handleClickMenuOption = (option: string, contactId: number) => async () => {
		switch (option) {
			case MENU_OPTION.DETAILS:
				handleOpenContactDetailsModal(contactId);
				break;

			case MENU_OPTION.DELETE:
				setDeleteContactId(contactId);
				break;

			default:
				break;
		}
	};

	return (
		<Box sx={contactListTableStyle.wrapper}>
			<SearchHeader
				page={page}
				items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
				total={totalCount}
				searchPlaceholder="Search contacts"
				onPressEnter={handlePressEnter}
				onSearchChange={handleSearchChange}
				unitName={TABLES_ENTITIES.CONTACTS}
				actionButton={
					<Button
						onClick={() => setIsShowImportContactsModal(true)}
						title={IMPORT_CONTACT_LIST}
						startIcon={<ImportIcon />}
						sx={contactListTableStyle.searchButton}
					/>
				}
			/>

			{isDataLoaded ? (
				totalCount ? (
					<Box sx={contactListTableStyle.tableContent}>
						<TableContainer sx={contactListTableStyle.container}>
							<Table sx={contactListTableStyle.table}>
								<TableHead>
									<TableRow sx={contactListTableStyle.tableHeader}>
										<TableCell>{ID}</TableCell>
										<TableCell>{FIRST_NAME}</TableCell>
										<TableCell>{LAST_NAME}</TableCell>
										<TableCell>{EMAIL}</TableCell>
										<TableCell>{PHONE}</TableCell>
										<TableCell>{MEAL}</TableCell>
										<TableCell />
									</TableRow>
								</TableHead>

								<TableBody>
									{data.map((row) => (
										<TableRow key={row.id} sx={contactListTableStyle.tableBody}>
											<TableCell>{row.id}</TableCell>
											<TableCell sx={contactListTableStyle.tableCell}>{row.firstName}</TableCell>
											<TableCell sx={contactListTableStyle.tableCell}>{row.lastName}</TableCell>
											<TableCell sx={contactListTableStyle.tableCell}>{row.email}</TableCell>
											<TableCell sx={contactListTableStyle.tableCell}>{row.phoneNumber}</TableCell>
											<TableCell sx={contactListTableStyle.tableCell}>{row.meals.join(', ')}</TableCell>

											<TableCell>
												<ActionsMenu
													disableRipple
													content={
														<Box>
															{tableContactsMenuContent.map((item) => (
																<Typography
																	key={item}
																	onClick={handleClickMenuOption(item, row.id)}
																	variant={BODY1}
																	sx={contactListTableStyle.menuItem}
																>
																	{item}
																</Typography>
															))}
														</Box>
													}
												/>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						<Box sx={contactListTableStyle.pagination}>
							<Pagination page={page} pageSize={pageSize} total={totalCount} onChange={(step) => setPage(step)} />
						</Box>
					</Box>
				) : (
					<EmptyTableData />
				)
			) : (
				<Loader parentClassName={contactListTableStyle.loader} size={50} />
			)}

			{isShowImportContactsModal && (
				<ImportContactList
					onCloseModal={handleImportContactsModalChange}
					open={isShowImportContactsModal}
					handleErrorModal={handleExitModalChange}
				/>
			)}
			<Modal
				title="Edit Contact"
				onClose={handleCloseContactModals}
				open={isShowContactEditModal}
				sx={contactListTableStyle.contactModal}
			>
				<ContactEdit detailsContact={detailsContact} onModalClose={handleCloseContactModals} />
			</Modal>

			<Modal
				onClose={handleCloseContactModals}
				open={isShowContactDetailsModal && !!detailsContact}
				sx={contactListTableStyle.contactModal}
				closeButtonTitle={CLOSE}
				mainButtonTitle={EDIT}
				onSave={handleOpenEditContactModal}
				isShowCloseButton
				isShowSaveButton
			>
				<ContactDetails detailsContact={detailsContact} />
			</Modal>

			<Modal
				onClose={() => setDeleteContactId(null)}
				onSave={handleDeleteContactItem}
				open={!!deleteContactId}
				sx={contactListTableStyle.deleteContactModal}
				color={ERROR}
				closeButtonTitle={CANCEL}
				mainButtonTitle={DELETE}
				isShowCloseButton
				isShowSaveButton
				mainIcon={<DangerIcon />}
				title="Delete Contact"
				description="Are you sure you want to delete this contact?"
			/>

			<Modal
				open={isExitModalOpen}
				onClose={handleExitModalChange}
				onSave={() => {
					handleExitModalChange();
					handleImportContactsModalChange();
				}}
				sx={contactListTableStyle.deleteContactModal}
				title="Exit Import"
				description="Are you sure you want to exit? Your current information will not be saved."
				mainButtonTitle="Exit"
				closeButtonTitle="Cancel"
				mainIcon={<DangerIcon />}
				color="error"
				isShowSaveButton
				isShowCloseButton
			/>
		</Box>
	);
};

export default ContactListTable;
